import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  ShoppingBag,
  MapPin,
  Plus,
  Search,
  Loader2,
  Dog,
  ArrowLeftRight,
  DollarSign,
  Star,
  Store,
  Package,
  Wrench,
  Filter,
  SlidersHorizontal,
  ExternalLink,
  Phone,
  Mail,
  Globe,
  Clock,
  Crown,
  BadgeCheck,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import ImageUploadInput from '../components/ImageUploadInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Marketplace = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isApproved = user && (user.is_beta_user || user.is_charter_member || user.is_admin || user.is_sysadmin || user.is_operator_admin);
  const [items, setItems] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || 'all');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [sortBy, setSortBy] = useState('newest');
  const [minRating, setMinRating] = useState(0);
  const [activeTab, setActiveTab] = useState('products');
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialogs
  const [createItemOpen, setCreateItemOpen] = useState(false);
  const [createShopOpen, setCreateShopOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [userShop, setUserShop] = useState(null);
  
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    price: '',
    category: 'pets',
    item_type: 'product',
    condition: 'healthy',
    location: '',
    images: [],
    is_trade: false
  });
  
  const [newShop, setNewShop] = useState({
    name: '',
    description: '',
    category: 'pet_supplies',
    logo_url: '',
    banner_url: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    location: '',
    business_hours: '',
    services_offered: []
  });
  
  const [imageUrl, setImageUrl] = useState('');
  const [serviceInput, setServiceInput] = useState('');

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'food', label: 'Dog Food' },
    { value: 'treats', label: 'Treats' },
    { value: 'toys', label: 'Toys' },
    { value: 'beds', label: 'Beds & Furniture' },
    { value: 'collar', label: 'Collars & Leashes' },
    { value: 'grooming', label: 'Grooming' },
    { value: 'accessory', label: 'Accessories' },
    { value: 'service', label: 'Services' }
  ];

  const shopCategories = [
    { value: 'pet-store', label: 'Pet Store' },
    { value: 'grooming', label: 'Grooming Salon' },
    { value: 'training', label: 'Dog Training' },
    { value: 'boarding', label: 'Boarding & Daycare' },
    { value: 'veterinary', label: 'Veterinary' },
    { value: 'supplies', label: 'Pet Supplies' },
    { value: 'other', label: 'Other' }
  ];

  const conditions = [
    { value: 'new', label: 'New' },
    { value: 'like-new', label: 'Like New' },
    { value: 'good', label: 'Good' },
    { value: 'fair', label: 'Fair' },
    { value: 'used', label: 'Used' }
  ];

  useEffect(() => {
    fetchData();
    if (user) {
      fetchUserShop();
    }
  }, [categoryFilter, typeFilter, sortBy, minRating]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (typeFilter !== 'all') params.append('item_type', typeFilter);
      if (searchQuery) params.append('q', searchQuery);
      if (minRating > 0) params.append('min_rating', minRating.toString());
      params.append('sort_by', sortBy);
      
      const [itemsRes, shopsRes] = await Promise.all([
        axios.get(`${API}/marketplace?${params.toString()}`),
        axios.get(`${API}/shops${searchQuery ? `?q=${searchQuery}` : ''}`)
      ]);
      setItems(itemsRes.data);
      setShops(shopsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserShop = async () => {
    try {
      const response = await axios.get(`${API}/shops/user/${user.id}`);
      setUserShop(response.data);
    } catch (error) {
      // User doesn't have a shop
    }
  };

  const handleSearch = () => {
    fetchData();
  };

  const handleAddImage = () => {
    if (imageUrl.trim() && newItem.images.length < 5) {
      setNewItem({ ...newItem, images: [...newItem.images, imageUrl.trim()] });
      setImageUrl('');
    }
  };

  const handleAddService = () => {
    if (serviceInput.trim()) {
      setNewShop({ ...newShop, services_offered: [...newShop.services_offered, serviceInput.trim()] });
      setServiceInput('');
    }
  };

  const handleCreateItem = async () => {
    if (!newItem.title.trim() || !newItem.description.trim() || !newItem.location.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!newItem.is_trade && (!newItem.price || parseFloat(newItem.price) <= 0)) {
      toast.error('Please set a valid price');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/marketplace`, {
        ...newItem,
        price: parseFloat(newItem.price) || 0
      });
      setItems([response.data, ...items]);
      setNewItem({
        title: '',
        description: '',
        price: '',
        category: 'pets',
        item_type: 'product',
        condition: 'healthy',
        location: '',
        images: [],
        is_trade: false
      });
      setCreateItemOpen(false);
      toast.success('Listing created!');
    } catch (error) {
      toast.error('Failed to create listing');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateShop = async () => {
    if (!newShop.name.trim() || !newShop.description.trim() || !newShop.location.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/shops`, newShop);
      setUserShop(response.data);
      setShops([response.data, ...shops]);
      setNewShop({
        name: '',
        description: '',
        category: 'pet_supplies',
        logo_url: '',
        banner_url: '',
        contact_email: '',
        contact_phone: '',
        website: '',
        location: '',
        business_hours: '',
        services_offered: []
      });
      setCreateShopOpen(false);
      toast.success('Shop created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shop');
    } finally {
      setCreating(false);
    }
  };

  const renderStars = (rating, size = 'w-4 h-4') => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${
              star <= rating
                ? 'text-warning fill-warning'
                : star <= rating + 0.5
                ? 'text-warning fill-warning/50'
                : 'text-muted-foreground/30'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Marketplace"
        description="Buy and sell dog-related items in the AADC marketplace. Supplies, accessories, and more from trusted community members."
        url="/marketplace"
      />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-4xl font-heading font-medium mb-2">Marketplace</h1>
            <p className="text-muted-foreground">
              Buy, sell, or trade dog supplies, accessories, and services
            </p>
            <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Crown className="w-3 h-3 text-yellow-500" />
                Full members can sell
              </span>
              <span className="flex items-center gap-1">
                <BadgeCheck className="w-3 h-3 text-blue-500" />
                Verified users can buy
              </span>
            </div>
          </div>

          {user && isApproved && (
            <div className="flex gap-2">
              {!userShop && (
                <Dialog open={createShopOpen} onOpenChange={setCreateShopOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="create-shop-btn">
                      <Store className="w-4 h-4 mr-2" />
                      Create Shop
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-heading">Create Your Shop</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Shop Name *</Label>
                        <Input
                          placeholder="Happy Paws Pet Store"
                          value={newShop.name}
                          onChange={(e) => setNewShop({ ...newShop, name: e.target.value })}
                          data-testid="shop-name-input"
                          className="input-field"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Description *</Label>
                        <Textarea
                          placeholder="Tell customers about your business..."
                          value={newShop.description}
                          onChange={(e) => setNewShop({ ...newShop, description: e.target.value })}
                          rows={3}
                          data-testid="shop-description-input"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Category</Label>
                          <Select
                            value={newShop.category}
                            onValueChange={(val) => setNewShop({ ...newShop, category: val })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {shopCategories.map(cat => (
                                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Location *</Label>
                          <Input
                            placeholder="City, State"
                            value={newShop.location}
                            onChange={(e) => setNewShop({ ...newShop, location: e.target.value })}
                            className="input-field"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <ImageUploadInput
                          label="Logo"
                          value={newShop.logo_url || ''}
                          onChange={(url) => setNewShop({ ...newShop, logo_url: url })}
                          kind="logo"
                          placeholder="https://..."
                          testId="shop-logo"
                        />
                        <ImageUploadInput
                          label="Banner"
                          value={newShop.banner_url || ''}
                          onChange={(url) => setNewShop({ ...newShop, banner_url: url })}
                          kind="banner"
                          placeholder="https://..."
                          testId="shop-banner"
                          previewClassName="h-20 w-full object-cover rounded border"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Contact Email</Label>
                          <Input
                            type="email"
                            placeholder="shop@email.com"
                            value={newShop.contact_email}
                            onChange={(e) => setNewShop({ ...newShop, contact_email: e.target.value })}
                            className="input-field"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            placeholder="+1 234 567 8900"
                            value={newShop.contact_phone}
                            onChange={(e) => setNewShop({ ...newShop, contact_phone: e.target.value })}
                            className="input-field"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Website</Label>
                        <Input
                          placeholder="https://yourshop.com"
                          value={newShop.website}
                          onChange={(e) => setNewShop({ ...newShop, website: e.target.value })}
                          className="input-field"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Business Hours</Label>
                        <Input
                          placeholder="Mon-Fri 9am-5pm, Sat 10am-3pm"
                          value={newShop.business_hours}
                          onChange={(e) => setNewShop({ ...newShop, business_hours: e.target.value })}
                          className="input-field"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Services Offered</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="e.g., Dog grooming"
                            value={serviceInput}
                            onChange={(e) => setServiceInput(e.target.value)}
                            className="input-field flex-1"
                          />
                          <Button type="button" variant="outline" onClick={handleAddService}>
                            Add
                          </Button>
                        </div>
                        {newShop.services_offered.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {newShop.services_offered.map((service, idx) => (
                              <Badge key={`shop-svc-${service}-${idx}`} variant="secondary" className="flex items-center gap-1">
                                {service}
                                <button onClick={() => setNewShop({
                                  ...newShop,
                                  services_offered: newShop.services_offered.filter((_, i) => i !== idx)
                                })}>×</button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      <Button 
                        className="w-full btn-primary"
                        onClick={handleCreateShop}
                        disabled={creating}
                        data-testid="submit-shop-btn"
                      >
                        {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        Create Shop
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {userShop && (
                <Button variant="outline" onClick={() => navigate(`/shop/${userShop.id}`)} data-testid="my-shop-btn">
                  <Store className="w-4 h-4 mr-2" />
                  My Shop
                </Button>
              )}

              <Dialog open={createItemOpen} onOpenChange={setCreateItemOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="btn-primary" 
                    data-testid="create-listing-btn"
                    disabled={user?.membership_tier === 'free'}
                    title={user?.membership_tier === 'free' ? 'Upgrade to full membership to sell' : ''}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {user?.membership_tier === 'free' ? 'Upgrade to Sell' : 'New Listing'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Create Listing</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        placeholder="e.g., Premium Dog Bed - Large"
                        value={newItem.title}
                        onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                        data-testid="listing-title-input"
                        className="input-field"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Description *</Label>
                      <Textarea
                        placeholder="Describe your item in detail..."
                        value={newItem.description}
                        onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                        rows={3}
                        data-testid="listing-description-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Select
                          value={newItem.item_type}
                          onValueChange={(val) => setNewItem({ ...newItem, item_type: val })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="product">Product</SelectItem>
                            <SelectItem value="service">Service</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select
                          value={newItem.category}
                          onValueChange={(val) => setNewItem({ ...newItem, category: val })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.slice(1).map(cat => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-2">
                        <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">Open to trades</span>
                      </div>
                      <Switch
                        checked={newItem.is_trade}
                        onCheckedChange={(checked) => setNewItem({ ...newItem, is_trade: checked })}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Price ($) {!newItem.is_trade && '*'}</Label>
                        <Input
                          type="number"
                          placeholder="0.00"
                          value={newItem.price}
                          onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                          disabled={newItem.is_trade}
                          className="input-field"
                          data-testid="listing-price-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Condition</Label>
                        <Select
                          value={newItem.condition}
                          onValueChange={(val) => setNewItem({ ...newItem, condition: val })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {conditions.map(cond => (
                              <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Location *</Label>
                      <Input
                        placeholder="City, State"
                        value={newItem.location}
                        onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                        className="input-field"
                        data-testid="listing-location-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Images (max 5)</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Image URL"
                          value={imageUrl}
                          onChange={(e) => setImageUrl(e.target.value)}
                          className="input-field flex-1"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={handleAddImage}
                          disabled={newItem.images.length >= 5}
                        >
                          Add
                        </Button>
                      </div>
                      {newItem.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {newItem.images.map((img, idx) => (
                            <div key={`img-${img.substring(0, 30)}-${idx}`} className="relative w-16 h-16 rounded overflow-hidden">
                              <img src={img} alt="" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs flex items-center justify-center"
                                onClick={() => setNewItem({
                                  ...newItem,
                                  images: newItem.images.filter((_, i) => i !== idx)
                                })}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <Button 
                      className="w-full btn-primary"
                      onClick={handleCreateItem}
                      disabled={creating}
                      data-testid="submit-listing-btn"
                    >
                      {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Create Listing
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Search and Filters */}
        {user && !isApproved && (
          <div className="mb-6 p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 flex items-center gap-3" data-testid="marketplace-restriction-banner">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">Marketplace buying and selling requires account approval. Browse items below, and contact an admin to get approved.</p>
          </div>
        )}
        <div className="space-y-4 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search products, services, and shops..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 input-field"
                data-testid="marketplace-search"
              />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Button onClick={handleSearch}>Search</Button>
          </div>

          {showFilters && (
            <Card className="p-4">
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="product">Products</SelectItem>
                      <SelectItem value="service">Services</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sort By</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest</SelectItem>
                      <SelectItem value="price_low">Price: Low to High</SelectItem>
                      <SelectItem value="price_high">Price: High to Low</SelectItem>
                      <SelectItem value="rating">Highest Rated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Min Rating: {minRating} stars</Label>
                  <Slider
                    value={[minRating]}
                    onValueChange={([val]) => setMinRating(val)}
                    max={5}
                    step={0.5}
                    className="mt-2"
                  />
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Products & Services ({items.length})
            </TabsTrigger>
            <TabsTrigger value="shops" className="flex items-center gap-2">
              <Store className="w-4 h-4" />
              Shops ({shops.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : items.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                    <ShoppingBag className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading text-xl mb-2">No listings found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchQuery ? 'Try different search terms' : 'Be the first to list something!'}
                  </p>
                  {user && (
                    <Button className="btn-primary" onClick={() => setCreateItemOpen(true)}>
                      Create First Listing
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {items.map((item) => (
                  <Card 
                    key={item.id}
                    className="card-interactive overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/marketplace/${item.id}`)}
                    data-testid={`listing-card-${item.id}`}
                  >
                    <div className="aspect-square bg-muted relative overflow-hidden">
                      {item.images && item.images.length > 0 ? (
                        <img 
                          src={item.images[0]}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {item.item_type === 'service' ? (
                            <Wrench className="w-16 h-16 text-muted-foreground/30" />
                          ) : (
                            <Dog className="w-16 h-16 text-muted-foreground/30" />
                          )}
                        </div>
                      )}
                      <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                        <Badge className="bg-primary/90 text-primary-foreground">
                          {item.category}
                        </Badge>
                        {item.item_type === 'service' && (
                          <Badge className="bg-secondary/90 text-secondary-foreground">
                            Service
                          </Badge>
                        )}
                        {item.is_trade && (
                          <Badge variant="outline" className="bg-background/80">
                            <ArrowLeftRight className="w-3 h-3 mr-1" />
                            Trade
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <CardContent className="p-4">
                      <h3 className="font-heading text-lg font-medium mb-1 line-clamp-1">{item.title}</h3>
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-3">{item.description}</p>
                      
                      {/* Rating */}
                      {item.rating_count > 0 && (
                        <div className="flex items-center gap-2 mb-3">
                          {renderStars(item.average_rating)}
                          <span className="text-sm text-muted-foreground">
                            ({item.rating_count})
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="price-tag flex items-center">
                          <DollarSign className="w-5 h-5" />
                          {item.price.toFixed(2)}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate max-w-[100px]">{item.location}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
                        <span>@{item.seller_username}</span>
                        <span>{formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="shops">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : shops.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                    <Store className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading text-xl mb-2">No shops found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchQuery ? 'Try different search terms' : 'Be the first to create a shop!'}
                  </p>
                  {user && !userShop && (
                    <Button className="btn-primary" onClick={() => setCreateShopOpen(true)}>
                      Create Your Shop
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {shops.map((shop) => (
                  <Card 
                    key={shop.id}
                    className="card-interactive overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/shop/${shop.id}`)}
                    data-testid={`shop-card-${shop.id}`}
                  >
                    {/* Banner */}
                    <div className="h-24 bg-gradient-to-br from-primary/20 to-secondary/20 relative">
                      {shop.banner_url && (
                        <img 
                          src={shop.banner_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      )}
                      {/* Logo */}
                      <div className="absolute -bottom-8 left-4">
                        <div className="w-16 h-16 rounded-xl bg-card border-4 border-card overflow-hidden">
                          {shop.logo_url ? (
                            <img src={shop.logo_url} alt={shop.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                              <Store className="w-8 h-8 text-primary" />
                            </div>
                          )}
                        </div>
                      </div>
                      {shop.is_verified && (
                        <Badge className="absolute top-2 right-2 bg-primary">Verified</Badge>
                      )}
                    </div>
                    
                    <CardContent className="pt-10 p-4">
                      <h3 className="font-heading text-lg font-medium mb-1">{shop.name}</h3>
                      <Badge variant="secondary" className="mb-2">{shop.category}</Badge>
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-3">{shop.description}</p>
                      
                      {/* Rating */}
                      <div className="flex items-center gap-2 mb-3">
                        {renderStars(shop.average_rating)}
                        <span className="text-sm text-muted-foreground">
                          {shop.average_rating.toFixed(1)} ({shop.rating_count} reviews)
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {shop.location}
                        </span>
                        <span>{shop.products_count} listings</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Marketplace;
